import { gql } from "@apollo/client";
import { Flex, Heading, Link } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { RouterLink } from "src/components/Links/RouterLink";
import { RemoteDataTable } from "src/components/Table/RemoteDataTable";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";

type Props = {
  userId: string;
  excludePersonId: string;
};
export const OtherProfiles: React.FC<Props> = ({ userId, excludePersonId }) => {
  const { remoteData } = useRemoteDataQuery<
    GQL.GetOtherProfilesByUserId,
    GQL.GetOtherProfilesByUserIdVariables
  >(
    gql`
      query GetOtherProfilesByUserId(
        $userId: String!
        $excludePersonId: uuid!
      ) {
        person(
          where: { user_id: { _eq: $userId }, id: { _neq: $excludePersonId } }
        ) {
          id
          first_name
          last_name
          email_address
          phone_number
          organization {
            id
            path
          }
          person_type
        }
      }
    `,
    {
      variables: {
        userId,
        excludePersonId,
      },
    }
  );

  const columns: ColumnDef<GQL.GetOtherProfilesByUserId["person"][0]>[] =
    useMemo(() => {
      return [
        {
          id: "id",
          cell: (props) => {
            const path = props.row.original.organization?.path;
            const id = props.row.original.id;
            const personType = props.row.original.person_type;

            if (!path) {
              return id;
            }

            let to: string | undefined = undefined;
            switch (personType) {
              case GQL.person_type_enum.guardian: {
                to = Url.OrgAdmin.Parents.edit({ path }, id);
                break;
              }
              case GQL.person_type_enum.admin:
              case GQL.person_type_enum.orgAdmin:
              case GQL.person_type_enum.districtAdmin:
              case GQL.person_type_enum.schoolAdmin: {
                to = Url.OrgAdmin.Team.editMember({ path }, id);
                break;
              }
              case GQL.person_type_enum.applicant: {
                to = Url.OrgAdmin.Students.edit({ path }, id);
                break;
              }
            }

            if (to === undefined) {
              return id;
            }

            return (
              <Link
                as={RouterLink}
                to={to}
                color="primary.500"
                textDecoration="underline"
              >
                {id}
              </Link>
            );
          },
        },
        {
          id: "organization",
          cell: (props) => props.row.original.organization?.path,
        },
        {
          id: "person type",
          cell: (props) => props.row.original.person_type,
        },
        {
          id: "first name",
          cell: (props) => props.row.original.first_name,
        },
        {
          id: "last name",
          cell: (props) => props.row.original.last_name,
        },
        {
          id: "email",
          cell: (props) => props.row.original.email_address,
        },
        {
          id: "phone number",
          cell: (props) => props.row.original.phone_number,
        },
      ];
    }, []);

  return (
    <Flex direction="column" gap="4">
      <Heading as="h3" variant="admin">
        Other profiles
      </Heading>
      <RemoteDataTable
        data={remoteData.map((data) => data.person)}
        noDataMessage="No other profiles"
        columns={columns}
      />
    </Flex>
  );
};
